import { oc } from "@orpc/contract";
import { z } from "zod";

const Outbound855MessageSchema = z.object({
  id: z.string(),
  transactionId: z.string().nullable(),
  error: z.boolean(),
  status: z.string().nullable(),
  createdAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable(),
  poNumber: z.string().nullable(),
  receiverIsaId: z.string().nullable(),
  senderIsaId: z.string().nullable(),
  /** The SalesOrder.internalId extracted from REF*VN in the 855 payload */
  salesOrderId: z.number().int().nullable(),
});

export type Outbound855MessageDTO = z.infer<typeof Outbound855MessageSchema>;

const ListOutbound855MessagesInputSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
});

// ---------------------------------------------------------------------------
// Shared schemas
// ---------------------------------------------------------------------------

const PageInfoSchema = z.object({
  currentPage: z.number().int(),
  totalPages: z.number().int(),
  totalCount: z.number().int(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});

const ListOutbound855MessagesOutputSchema = z.object({
  edges: z.array(Outbound855MessageSchema),
  pageInfo: PageInfoSchema,
});

export type ListOutbound855MessagesOutput = z.infer<
  typeof ListOutbound855MessagesOutputSchema
>;

const Resend855InputSchema = z.object({
  salesOrderId: z.number().int().positive(),
});

const Resend855OutputSchema = z.object({
  /** true = 855 was sent, false = 855 was skipped (disabled for customer) */
  sent: z.boolean(),
});

// ---------------------------------------------------------------------------
// Famous Sales Orders (EDI Invoicing)
// ---------------------------------------------------------------------------

const OrderFetchStatusSchema = z.enum([
  "pending",
  "fetching",
  "success",
  "failed",
]);

const EdiInvoiceTypeSchema = z.enum(["810", "880"]);

const FamousSalesOrderSchema = z.object({
  kind: z.literal("famous_sales_order"),
  id: z.string().uuid(),
  famousSalesOrderId: z.string(),
  orderDetails: z.unknown().nullable(),
  orderFetchStatus: OrderFetchStatusSchema,
  orderFetchError: z.record(z.string(), z.unknown()).nullable(),
  edi810SentAt: z.coerce.date().nullable(),
  edi810Status: z.string().nullable(),
  edi810StatusLastFetchedAt: z.coerce.date().nullable(),
  ediInvoiceSentAt: z.coerce.date().nullable(),
  ediInvoiceStatus: z.string().nullable(),
  ediInvoiceTransactionId: z.string().nullable(),
  ediInvoiceValidationStatus: z.string().nullable(),
  ediInvoiceDeliveryStatus: z.string().nullable(),
  ediInvoiceStatusLastFetchedAt: z.coerce.date().nullable(),
  ediInvoiceType: EdiInvoiceTypeSchema.nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  salesOrderId: z.string().uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type FamousSalesOrderDTO = z.infer<typeof FamousSalesOrderSchema>;

export const EdiSendStatusSchema = z.enum(["sent", "error", "not_sent"]);
export type EdiSendStatus = z.infer<typeof EdiSendStatusSchema>;

const ListFamousSalesOrdersInputSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  orderFetchStatus: OrderFetchStatusSchema.optional(),
  famousSalesOrderId: z.string().optional(),
  purchaseOrderNumber: z.string().optional(),
  id: z.string().uuid().optional(),
  customerId: z.string().uuid().optional(),
  ediInvoiceType: EdiInvoiceTypeSchema.optional(),
  ediSendStatus: EdiSendStatusSchema.optional(),
  importedFrom: z.coerce.date().optional(),
  importedTo: z.coerce.date().optional(),
});

export type ListFamousSalesOrdersInput = z.infer<
  typeof ListFamousSalesOrdersInputSchema
>;

const ListFamousSalesOrdersOutputSchema = z.object({
  edges: z.array(FamousSalesOrderSchema),
  pageInfo: PageInfoSchema,
});

export type ListFamousSalesOrdersOutput = z.infer<
  typeof ListFamousSalesOrdersOutputSchema
>;

const RefreshFamousSalesOrderInputSchema = z.object({
  famousSalesOrderId: z.string().uuid(),
  // Admin-only. When true, bypasses the "already sent" lock and clears the
  // sent-tracking fields so the invoice can be re-sent after refresh.
  force: z.boolean().optional(),
});

const RefreshFamousSalesOrderOutputSchema = FamousSalesOrderSchema;

export type RefreshFamousSalesOrderInput = z.infer<
  typeof RefreshFamousSalesOrderInputSchema
>;

export type RefreshFamousSalesOrderOutput = z.infer<
  typeof RefreshFamousSalesOrderOutputSchema
>;

// ---------------------------------------------------------------------------
// Orderful Config CRUD
// ---------------------------------------------------------------------------

const EdiConfigKeySchema = z.enum(["850", "855", "810", "856", "880", "875"]);

export type EdiConfigKey = z.infer<typeof EdiConfigKeySchema>;

export const UpsertOrderfulConfigInputSchema = z.object({
  customerId: z.string().uuid(),
  key: EdiConfigKeySchema,
  value: z.record(z.string(), z.unknown()),
});

export type UpsertOrderfulConfigInput = z.infer<
  typeof UpsertOrderfulConfigInputSchema
>;

const UpsertOrderfulConfigOutputSchema = z.object({
  id: z.string().uuid(),
  key: EdiConfigKeySchema,
  customerId: z.string().uuid(),
  value: z.record(z.string(), z.unknown()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type UpsertOrderfulConfigOutput = z.infer<
  typeof UpsertOrderfulConfigOutputSchema
>;

export const DeleteOrderfulConfigInputSchema = z.object({
  customerId: z.string().uuid(),
  key: EdiConfigKeySchema,
});

export type DeleteOrderfulConfigInput = z.infer<
  typeof DeleteOrderfulConfigInputSchema
>;

const DeleteOrderfulConfigOutputSchema = z.object({
  success: z.boolean(),
});

export type DeleteOrderfulConfigOutput = z.infer<
  typeof DeleteOrderfulConfigOutputSchema
>;

// ---------------------------------------------------------------------------
// Partner presets — bulk-apply known-good config bundles for a trading partner
// ---------------------------------------------------------------------------

export const PARTNER_PRESET_IDS = ["procurant"] as const;
export const PartnerPresetIdSchema = z.enum(PARTNER_PRESET_IDS);
export type PartnerPresetId = z.infer<typeof PartnerPresetIdSchema>;

export const ApplyPartnerPresetInputSchema = z.object({
  customerId: z.string().uuid(),
  presetId: PartnerPresetIdSchema,
});

export type ApplyPartnerPresetInput = z.infer<
  typeof ApplyPartnerPresetInputSchema
>;

const ApplyPartnerPresetOutputSchema = z.object({
  /** EDI keys that had their config upserted */
  applied: z.array(EdiConfigKeySchema),
});

export type ApplyPartnerPresetOutput = z.infer<
  typeof ApplyPartnerPresetOutputSchema
>;

// ---------------------------------------------------------------------------
// Manual order import — replay an Orderful 850/875 through the poller pipeline
// ---------------------------------------------------------------------------

const ImportableOrderTypeSchema = z.enum([
  "850_PURCHASE_ORDER",
  "875_GROCERY_PRODUCTS_PURCHASE_ORDER",
]);

export const ImportOrderfulTransactionInputSchema = z.object({
  // Orderful transaction IDs are numeric and get interpolated into the GET URL;
  // reject anything non-numeric (incl. path-altering chars) before it's used.
  transactionId: z
    .string()
    .trim()
    .regex(/^\d+$/, "Transaction ID must be numeric"),
});

export type ImportOrderfulTransactionInput = z.infer<
  typeof ImportOrderfulTransactionInputSchema
>;

const ImportOrderfulTransactionOutputSchema = z.object({
  orderfulMessageId: z.string().uuid(),
  stagedPurchaseOrderId: z.string().uuid(),
  purchaseOrderNumber: z.string(),
  transactionType: ImportableOrderTypeSchema,
  stream: z.string(),
  customerName: z.string().nullable(),
});

export type ImportOrderfulTransactionOutput = z.infer<
  typeof ImportOrderfulTransactionOutputSchema
>;

// ---------------------------------------------------------------------------
// Famous Order Confirmations (EDI 855)
// ---------------------------------------------------------------------------

const FamousOrderConfirmationSchema = z.object({
  id: z.string().uuid(),
  famousOrderId: z.string(),
  orderDetails: z.unknown().nullable(),
  orderFetchStatus: OrderFetchStatusSchema,
  orderFetchError: z.record(z.string(), z.unknown()).nullable(),
  edi855SentAt: z.coerce.date().nullable(),
  edi855Status: z.string().nullable(),
  edi855TransactionId: z.string().nullable(),
  edi855Error: z.string().nullable(),
  edi855StatusLastFetchedAt: z.coerce.date().nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type FamousOrderConfirmationDTO = z.infer<
  typeof FamousOrderConfirmationSchema
>;

const ListOrderConfirmationsInputSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  famousOrderId: z.string().optional(),
  customerId: z.string().uuid().optional(),
  edi855Status: z.enum(["not_sent", "pending", "sent", "error"]).optional(),
});

const ListOrderConfirmationsOutputSchema = z.object({
  edges: z.array(FamousOrderConfirmationSchema),
  pageInfo: PageInfoSchema,
});

export type ListOrderConfirmationsOutput = z.infer<
  typeof ListOrderConfirmationsOutputSchema
>;

const FetchOrderConfirmationInputSchema = z.object({
  famousOrderId: z.string(),
});

const FetchOrderConfirmationOutputSchema = FamousOrderConfirmationSchema;

const GetOrderConfirmationInputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
});

const GetOrderConfirmationOutputSchema = FamousOrderConfirmationSchema;

const SyncOrderConfirmationInputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
  // Admin-only. When true, bypasses the "already sent" lock and clears the
  // 855 sent-tracking fields so the confirmation can be re-sent after refresh.
  force: z.boolean().optional(),
});

const SyncOrderConfirmationOutputSchema = FamousOrderConfirmationSchema;

const DeleteOrderConfirmationInputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
});

const DeleteOrderConfirmationOutputSchema = z.object({
  success: z.boolean(),
});

const SendOrderConfirmation855InputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
  force: z.boolean().optional(),
});

const SendOrderConfirmation855OutputSchema = z.object({
  sent: z.boolean(),
});

// ---------------------------------------------------------------------------
// Organization-level EDI settings
// ---------------------------------------------------------------------------

/**
 * The organization's sender ISA ID (ISA06). Max 15 chars per X12; an empty
 * string is normalized to null (unset) by the handler.
 */
export const UpdateOrganizationIsaIdInputSchema = z.object({
  isaId: z.string().trim().max(15).nullable(),
});

export type UpdateOrganizationIsaIdInput = z.infer<
  typeof UpdateOrganizationIsaIdInputSchema
>;

const OrganizationIsaIdOutputSchema = z.object({
  isaId: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Customer-level EDI identity (ISA ID + vendor ID)
// ---------------------------------------------------------------------------

const CustomerEdiIdentityOutputSchema = z.object({
  isaId: z.string().nullable(),
  vendorId: z.string().nullable(),
});

export const GetCustomerEdiIdentityInputSchema = z.object({
  customerId: z.string().uuid(),
});

/**
 * Partial update of a customer's EDI identity. Omitting a field leaves it
 * unchanged; passing `null` clears it. Empty strings are normalized to null.
 */
export const UpdateCustomerEdiIdentityInputSchema = z.object({
  customerId: z.string().uuid(),
  isaId: z.string().trim().max(15).nullable().optional(),
  vendorId: z.string().trim().max(80).nullable().optional(),
});

export type UpdateCustomerEdiIdentityInput = z.infer<
  typeof UpdateCustomerEdiIdentityInputSchema
>;

// ---------------------------------------------------------------------------
// Orderful Transactions (admin) — live list of 810 transactions from Orderful,
// filtered by the org's configured vendor number(s) since Orderful itself
// can't filter by vendor id.
// ---------------------------------------------------------------------------

const OrderfulTransactionVendorSchema = z.object({
  customerId: z.string().uuid(),
  customerName: z.string(),
  vendorId: z.string(),
});

export type OrderfulTransactionVendorDTO = z.infer<
  typeof OrderfulTransactionVendorSchema
>;

const ListOrderfulTransactionVendorsOutputSchema = z.object({
  vendors: z.array(OrderfulTransactionVendorSchema),
});

const OrderfulTransactionSchema = z.object({
  /** Orderful transaction id. */
  id: z.string(),
  /** Orderful message type name, e.g. "850_PURCHASE_ORDER". */
  transactionType: z.string().nullable(),
  /** 3-digit EDI document code, e.g. "850". */
  documentCode: z.string().nullable(),
  stream: z.string().nullable(),
  senderIsaId: z.string().nullable(),
  receiverIsaId: z.string().nullable(),
  /** Configured customer this result was narrowed to, when a vendor is selected. */
  matchedCustomerName: z.string().nullable(),
  validationStatus: z.string().nullable(),
  deliveryStatus: z.string().nullable(),
  acknowledgmentStatus: z.string().nullable(),
  /** Deep link to the transaction in the Orderful dashboard. */
  orderfulUrl: z.string().nullable(),
  createdAt: z.coerce.date().nullable(),
});

export type OrderfulTransactionDTO = z.infer<typeof OrderfulTransactionSchema>;

const ListOrderfulTransactionsInputSchema = z.object({
  /**
   * A configured vendor id to narrow to (maps to an OrderfulApiService partner).
   * Omit to list all of this environment's transactions (tenant-scoped by the
   * service's local-record ownership check).
   */
  vendorId: z.string().trim().min(1).nullable().optional(),
  /** How far back to look, in days. */
  lookbackDays: z.number().int().min(1).max(365).default(30),
});

export type ListOrderfulTransactionsInput = z.infer<
  typeof ListOrderfulTransactionsInputSchema
>;

const ListOrderfulTransactionsOutputSchema = z.object({
  transactions: z.array(OrderfulTransactionSchema),
  /** True when more of this environment's transactions match than were returned. */
  truncated: z.boolean(),
  /** Notes from the service about how the answer was bounded (shown to the user). */
  notes: z.array(z.string()),
});

export type ListOrderfulTransactionsOutput = z.infer<
  typeof ListOrderfulTransactionsOutputSchema
>;

export const ediContracts = {
  organization: {
    getIsaId: oc.output(OrganizationIsaIdOutputSchema).route({ method: "GET" }),
    updateIsaId: oc
      .input(UpdateOrganizationIsaIdInputSchema)
      .output(OrganizationIsaIdOutputSchema)
      .route({ method: "POST" }),
  },

  customers: {
    getIdentity: oc
      .input(GetCustomerEdiIdentityInputSchema)
      .output(CustomerEdiIdentityOutputSchema)
      .route({ method: "GET" }),
    updateIdentity: oc
      .input(UpdateCustomerEdiIdentityInputSchema)
      .output(CustomerEdiIdentityOutputSchema)
      .route({ method: "POST" }),
  },

  outbound855Messages: {
    list: oc
      .input(ListOutbound855MessagesInputSchema)
      .output(ListOutbound855MessagesOutputSchema)
      .route({ method: "GET" }),

    resend: oc
      .input(Resend855InputSchema)
      .output(Resend855OutputSchema)
      .route({ method: "POST" }),
  },

  famousSalesOrders: {
    list: oc
      .input(ListFamousSalesOrdersInputSchema)
      .output(ListFamousSalesOrdersOutputSchema)
      .route({ method: "GET" }),

    refresh: oc
      .input(RefreshFamousSalesOrderInputSchema)
      .output(RefreshFamousSalesOrderOutputSchema)
      .route({ method: "POST" }),
  },

  orderConfirmations: {
    list: oc
      .input(ListOrderConfirmationsInputSchema)
      .output(ListOrderConfirmationsOutputSchema)
      .route({ method: "GET" }),

    fetch: oc
      .input(FetchOrderConfirmationInputSchema)
      .output(FetchOrderConfirmationOutputSchema)
      .route({ method: "POST" }),

    get: oc
      .input(GetOrderConfirmationInputSchema)
      .output(GetOrderConfirmationOutputSchema)
      .route({ method: "GET" }),

    sync: oc
      .input(SyncOrderConfirmationInputSchema)
      .output(SyncOrderConfirmationOutputSchema)
      .route({ method: "POST" }),

    delete: oc
      .input(DeleteOrderConfirmationInputSchema)
      .output(DeleteOrderConfirmationOutputSchema)
      .route({ method: "POST" }),

    sendEdi855: oc
      .input(SendOrderConfirmation855InputSchema)
      .output(SendOrderConfirmation855OutputSchema)
      .route({ method: "POST" }),
  },

  orderfulConfigs: {
    upsert: oc
      .input(UpsertOrderfulConfigInputSchema)
      .output(UpsertOrderfulConfigOutputSchema)
      .route({ method: "POST" }),

    delete: oc
      .input(DeleteOrderfulConfigInputSchema)
      .output(DeleteOrderfulConfigOutputSchema)
      .route({ method: "POST" }),

    applyPreset: oc
      .input(ApplyPartnerPresetInputSchema)
      .output(ApplyPartnerPresetOutputSchema)
      .route({ method: "POST" }),
  },

  orderfulImport: {
    importTransaction: oc
      .input(ImportOrderfulTransactionInputSchema)
      .output(ImportOrderfulTransactionOutputSchema)
      .route({ method: "POST" }),
  },

  orderfulTransactions: {
    listVendors: oc
      .output(ListOrderfulTransactionVendorsOutputSchema)
      .route({ method: "GET" }),

    list: oc
      .input(ListOrderfulTransactionsInputSchema)
      .output(ListOrderfulTransactionsOutputSchema)
      .route({ method: "GET" }),
  },
} as const;

export type EdiContracts = typeof ediContracts;
